import { EventType, IEvent, IHasEventHandlers, EventHandlers } from 'js/bubbleCore/events';
import { Bubble } from 'js/bubbleCore/bubbles/types';
import { BubbleView } from 'js/bubbleCore/view/bubbles/BubbleView';
import { spacePositionToPxPosition } from 'js/bubbleCore/view/utils/spacePositionToPxPosition';
import { gridHexToPxPosition } from 'js/bubbleCore/view/utils/gridHexToPxPosition';
import { IHexPixelSize } from 'js/bubbleCore/view/types';
import { IHexGridData, Place } from 'js/bubbleCore/types';
import { loopGrid } from 'js/bubbleCore/utils/loopGrid';
import { TweenManager } from 'framework/TweenManager/TweenManager';
import { Easing } from 'framework/TweenManager/Easing';
import { filterGrid } from 'js/bubbleCore/utils/filterGrid';
import { getDistance, getAngle, lengthDirX, lengthDirY, getCentroid } from 'framework/utils/math-utils';
import { BubbleGameBoardView } from 'js/bubbleCore/view/BubbleGameBoardView';
import { Layout } from 'js/layouts/GameBoardLayout';

interface IBubblesViewConfig {

}

export class BubblesView implements IHasEventHandlers {
  public readonly eventHandlers: EventHandlers = {
    [EventType.BubbleUpdated]: (e) => this.updateBubble(e.data.bubble),
    [EventType.BubbleDestroyed]: async (e) => {
      const view = this.getViewForBubble(e.data.bubble);
      await view.animateKill(e);
      this.destroyView(e.data.bubble);
    },
    [EventType.BubbleTransfered]: ({data}) => {
      const view = this.getViewForBubble(data.bubble);
      this.moveToProperContainer(view, data.bubble.place);
      this.updateBubble(data.bubble);
      if (data.toPlace === Place.PopOutSpace && data.fromPlace === Place.Grid) {
        view.animatePopOut();
      }
    },
    [EventType.GridOffsetChanged]: (e) => {
      this.layout.bubblesGridContainer.position.set(
       e.data.grid.offset.x * this.hexPixelSize.width,
       e.data.grid.offset.y * this.hexPixelSize.height,
      );
    },
    [EventType.UnsuccessfulMove]: (e) => {
      this.bounceBackToGridPosition(e.data.shootedBubble);
      this.waveBubbles([e.data.shootedBubble], 100, 0.2);
    },
    [EventType.SuccessfulMove]: (e) => {
      this.waveBubbles(e.data.bubblesToPopout, 150, 0.1);
    },
  };

  private readonly boardView: BubbleGameBoardView;
  private readonly tweenManager: TweenManager;
  private readonly gridModel: IHexGridData<Bubble>;
  private readonly idToView: Map<string, BubbleView> = new Map();
  private readonly hexPixelSize: IHexPixelSize;
  private readonly layout: Layout;

  constructor(boardView: BubbleGameBoardView) {
    this.boardView = boardView;
    this.tweenManager = boardView.tweenManager;
    this.hexPixelSize = boardView.getHexPixelSize();
    this.gridModel = boardView.getData().grid;
    this.layout = boardView.getLayout();
    this.init();
  }

  private init(): void {
    loopGrid(this.gridModel, (model, col, row) => this.updateBubble(model));
  }

  private getViewForBubble(bubble: Bubble): BubbleView {
    return this.idToView.get(bubble.id) || this.createBubbleView(bubble);
  }

  private createBubbleView(bubble: Bubble): BubbleView {
    const view = new BubbleView({boardView: this.boardView});
    view.reset(bubble);
    this.idToView.set(bubble.id, view);
    this.moveToProperContainer(view, bubble.place);
    return view;
  }

  private moveToProperContainer(view: BubbleView, place: Place): void {
    const target: PIXI.Container = {
      [Place.Grid]: this.layout.bubblesGridContainer,
      [Place.ShootingSpace]: this.layout.bubblesShootedContainer,
      [Place.PopOutSpace]: this.layout.bubblesPopOutContainer,
    }[place];

    if (view.parent === target) return;

    target.addChild(view);
    if (place === Place.Grid) this.sortGridContainer();
  }

  private destroyView(bubble: Bubble): void {
    const view = this.idToView.get(bubble.id);
    if (view) view.destroy();
    this.idToView.delete(bubble.id);
  }

  private updateBubble(model: Bubble): void {
    const view = this.getViewForBubble(model);
    if (model.place === Place.Grid) {
      gridHexToPxPosition(
        this.gridModel,
        model.col,
        model.row,
        this.hexPixelSize,
        false,
        view.position as PIXI.Point,
      );
    } else {
      spacePositionToPxPosition(
        model.hexSpaceX,
        model.hexSpaceY,
        this.hexPixelSize,
        view.position as PIXI.Point
      );
    }
  }

  public debugUpdate(): void {
    loopGrid(this.gridModel, (bubble) => this.updateBubble(bubble));
  }

  public bounceBackToGridPosition(model: Bubble): void {
    const view = this.getViewForBubble(model);
    const positionWhenCollided = spacePositionToPxPosition(
      model.hexSpaceX,
      model.hexSpaceY,
      this.hexPixelSize,
    );
    positionWhenCollided.x -= this.gridModel.offset.x * this.hexPixelSize.width;
    positionWhenCollided.y -= this.gridModel.offset.y * this.hexPixelSize.height;
    view.pivotTweenStack.addTween(
      this.tweenManager.create({
        x: view.x - positionWhenCollided.x,
        y: view.y - positionWhenCollided.y,
      }).to({x: 0, y: 0}, 300, Easing.Elastic.Out, true)
    );
  }

  private sortGridContainer(): void {
    this.layout.bubblesGridContainer.children.sort((itemA, itemB) => itemA.y - itemB.y)
  }

  private waveBubbles(sources: Bubble[], distanceThreshold: number, multi = 0.1): void {
    const source = getCentroid(sources.map((bubble) => {
      const view = this.getViewForBubble(bubble);
      return view.position;
    }));

    filterGrid(this.gridModel, (otherBubble) => {
      if (sources.includes(otherBubble)) return false;
      const otherView = this.getViewForBubble(otherBubble);
      return getDistance(source, otherView) < distanceThreshold;
    }).forEach((otherBubble) => {
      const otherView = this.getViewForBubble(otherBubble);
      const angle = getAngle(otherView, source);
      const distance = getDistance(source, otherView);

      const power = Math.sqrt((distanceThreshold - distance) * multi);
      otherView.pivotTweenStack.addTween(
        this.tweenManager.create({x: 0, y: 0}).to({
          x: lengthDirX(angle, power, true),
          y: lengthDirY(angle, power, true),
        }, 150, Easing.Sinusoidal.Out, true, 0, 0, true)
      );
    });
  }
}